<template>
  <q-select
    selected-paginated
    class="bg-neutral-10 size-md label-transparent"
    dropdown-icon="expand_more"
    clear-icon="clear"
    :fill-input="false"
    :input-debounce="inputDebounce"
    :options="options"
    :loading="loading"
    use-input
    map-options
    :option-value="optionValue"
    :option-label="optionLabel"
    :label="label"
    :model-value="null"
    @virtual-scroll="handleVirtualScroll"
    @filter="filterFn"
    @focus="updateOnFocus && fetchInitialOptions()"
  >
    <template #no-option>
      <div v-if="!loading" class="!my-26">
        <EmptyItem />
      </div>
    </template>

    <template v-for="slot in Object.keys(slots)" #[slot]="slotProps">
      <slot :name="slot" v-bind="slotProps || {}" />
    </template>
  </q-select>
</template>

<script setup>
import { api } from 'js/modules/axios'
import { onMounted, ref, useSlots } from 'vue'
import { stringifyUrl } from 'js/utils/GLOBAL'
import EmptyItem from 'components/Empty/EmptyItem.vue'
import queryString from 'query-string'

const slots = useSlots()

const props = defineProps({
  apiUrl: { type: String, required: true },
  // /** Id para diferenciar as requisicoes */
  // selectID: {
  //   type: String,
  //   required: true,
  // },

  label: { type: String, default: null },
  optionValue: { type: String, default: 'id' },
  optionLabel: { type: String, default: 'nome' },
  inputDebounce: { type: Number, default: 300 },
  searchParam: { type: String, default: 'search' },
  limitParam: { type: String, default: 'page_size' },
  queryParam: { type: Object, default: () => ({}) },
  transformOptions: { type: Function, default: (v) => v },
  adictionalFilters: { type: Object, default: () => ({}) },
  updateOnFocus: { type: Boolean, default: false },
  initialLimit: { type: Number, default: 20 },
})

const options = ref([])
const loading = ref(false)
const nextUrl = ref(null)
const selectPaginated = ref(null)
const stringOptions = ref([]) // Opcões carregadas localmente

// Filter function (local + async)
const searchOld = ref(null)

const filterFn = async (val, update, abort) => {
  if (!val && !searchOld.value) return update()
  const deletedText = !val && searchOld.value

  if (deletedText) {
    searchOld.value = null
    resetOptions()
    fetchInitialOptions()
    return update()
  }

  // Debounce na busca para evitar requisições excessivas
  if (loading.value) {
    abort()
    return
  }
  searchOld.value = val
  nextUrl.value = ''
  loading.value = true
  try {
    const params = {
      ...props.queryParam,
      [props.searchParam]: val,
      [props.limitParam]: props.initialLimit,
      ...props.adictionalFilters,
    }
    const url = stringifyUrl({ url: props.apiUrl, query: params })

    const { data } = await api.get(url)
    const response = data || {}
    const results = response?.results || []

    options.value = props.transformOptions(results) // Atualiza as opções com os resultados da busca
    update(() => {})
  } catch (error) {
    console.error('Erro na busca:', error)
  } finally {
    loading.value = false
  }
}

const fetchInitialOptions = async (query) => {
  if (loading.value) return

  loading.value = true
  try {
    const params = {
      ...props.queryParam,
      [props.searchParam]: query || '',
      [props.limitParam]: props.initialLimit,
      ...props.adictionalFilters,
    }
    const url = stringifyUrl({ url: props.apiUrl, query: params })
    const { data } = await api.get(url)
    const request = data || {}
    const results = request.results || []

    // Se houver resultados, atualiza as opções
    if (results?.length > 0) {
      options.value = props.transformOptions(results)
      stringOptions.value = [
        ...stringOptions.value,
        props.transformOptions(results),
      ] // Armazena para filtro local
    }
    if (data.next) {
      const parsedUrl = queryString.parseUrl(data.next)
      nextUrl.value = stringifyUrl(parsedUrl)
    } else {
      nextUrl.value = undefined
    }
  } catch (error) {
    console.error('Erro ao buscar dados:', error)
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  if (!nextUrl.value || loading.value) return
  loading.value = true
  try {
    const { data } = await api.get(nextUrl.value)

    if (data?.results && data.results.length > 0) {
      options.value = props.transformOptions([
        ...options.value,
        ...props.transformOptions(data.results),
      ])
    }

    if (data.next) {
      const parsedUrl = queryString.parseUrl(data.next)
      nextUrl.value = stringifyUrl(parsedUrl)
    } else {
      nextUrl.value = undefined
    }
  } catch (error) {
    console.error('Erro ao buscar dados:', error)
  } finally {
    loading.value = false
  }
}

const resetOptions = () => {
  options.value = []
  stringOptions.value = []
}

const initializeOptions = async () => {
  resetOptions()
  fetchInitialOptions()
}

onMounted(() => {
  initializeOptions()
})

const handleVirtualScroll = (details) => {
  if (details.index < props.initialLimit - 1) return
  const scrollToLastItem =
    details.to >= options.value.length - 1 && !loading.value
  if (scrollToLastItem) loadMore()
}

defineExpose({ selectPaginated })
</script>

<style lang="sass" scoped>
.q-select--multiple
  :deep(.q-field__native)
    padding-top: .375rem !important

  &.q-field--focused, &.with-value
    :deep(.q-field__label)
      top: 0.5rem !important
      margin: 0 !important

:not(.q-field--focused) :deep(.q-field__append > i[aria-label="Limpar"])
  display: none !important

.q-field--float :deep(input::placeholder)
  opacity: 0 !important
</style>
